import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { ChapterEnum } from 'gql/graphql';
import { WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { addUerToActKC2PriceList, addUerToWorkOrderFromPriceList } from './workOrderPriceListUerQueries';

export const useAddUerFromWorkOrder = (
    shouldAddToActKC2: boolean,
    chapter: ChapterEnum,
    objectId: number,
    onCancel: () => void,
    reloadWorkOrder: () => void,
    uerInputs: {
        uerBaseRowId: number;
        rationale: string;
    }[],
    workOrderStatus?: WorkOrderStatusEnum,
) => {
    const dispatch = useDispatch();

    const { id: projectId, orderId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [isSubmitted, setIsSubmitted] = useState(false);

    // TODO: 22.02 изменили сигнатуру вызова
    const [
        addUerFromWorkOrderData,
        addUerFromWorkOrderStatus,
        addUerFromWorkOrderError,
        addUerFromWorkOrderMakeRequest,
    ] = useRequest({
        requestId: 'addUerFromWorkOrder',
        // @ts-ignore
        query: shouldAddToActKC2 ? addUerToActKC2PriceList : addUerToWorkOrderFromPriceList,
        withEffect: false,
    });

    const addUerData = shouldAddToActKC2
        ? // @ts-ignore
          addUerFromWorkOrderData?.uerAddUerToActKC2PriceList
        : addUerFromWorkOrderData?.uerAddUerToWorkOrderFromPriceList;

    const addUerResult = shouldAddToActKC2
        ? // @ts-ignore
          addUerFromWorkOrderData?.uerAddUerToActKC2PriceList?.result
        : addUerFromWorkOrderData?.uerAddUerToWorkOrderFromPriceList?.result;

    const addUerValidation = shouldAddToActKC2
        ? // @ts-ignore
          addUerFromWorkOrderData?.uerAddUerToActKC2PriceList?.validation
        : addUerFromWorkOrderData?.uerAddUerToWorkOrderFromPriceList?.validation;

    const isLoading = addUerFromWorkOrderStatus === RequestStatus.FETCHING;

    const [resetAddUerFromWorkOrder] = useResetStoreData({ requestId: 'addUerFromWorkOrder' });

    useEffect(() => {
        if (addUerResult && addUerFromWorkOrderStatus === RequestStatus.SUCCESS) {
            onCancel();
            reloadWorkOrder();
        }
    }, [addUerResult, addUerFromWorkOrderStatus, onCancel, reloadWorkOrder]);

    useEffect(
        () => () => {
            resetAddUerFromWorkOrder(undefined);
        },
        [resetAddUerFromWorkOrder],
    );

    const analyticsData = useMemo(
        () => ({
            projectId,
            orderId: Number(orderId),
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            orderId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
        ],
    );

    const uerBaseRowIds = useMemo(() => uerInputs.map((item) => item.uerBaseRowId), [uerInputs]);

    const handlerAddUerFromWorkOrder = useCallback(() => {
        if (shouldAddToActKC2) {
            // TODO: any
            dispatch(
                addUerFromWorkOrderMakeRequest({
                    chapter,
                    workOrderObjectId: objectId,
                    uerInputs,
                } as any),
            );

            sendGpnAnalyticsEvent({
                componentId: 'rim_act KC_add to order price button_pressed',
                ...analyticsData,
                orderStatus: WorkOrderStatusEnum.InProgress,
                addType: WorkOrderPriceTypeEnum.Uer,
            });
        } else if (!shouldAddToActKC2) {
            dispatch(
                addUerFromWorkOrderMakeRequest({
                    chapter,
                    workOrderObjectId: objectId,
                    uerBaseRowIds,
                }),
            );

            sendGpnAnalyticsEvent({
                componentId: 'rim_NZ_add to order price button_pressed',
                ...analyticsData,
                orderStatus: WorkOrderStatusEnum.OnFormation,
                addType: WorkOrderPriceTypeEnum.Uer,
            });
        }

        setIsSubmitted(true);
    }, [
        shouldAddToActKC2,
        dispatch,
        addUerFromWorkOrderMakeRequest,
        chapter,
        objectId,
        uerInputs,
        sendGpnAnalyticsEvent,
        analyticsData,
        uerBaseRowIds,
    ]);

    const handleCloseModalError = useCallback(() => {
        onCancel();
        setIsSubmitted(false);
    }, [onCancel]);

    const handleCreateReport = () => {
        ExportCSVReport(addUerValidation);
    };

    return {
        isSubmitted,
        isLoading,
        addUerData,
        addUerFromWorkOrderStatus,
        addUerFromWorkOrderError,
        handlerAddUerFromWorkOrder,
        handleCloseModalError,
        handleCreateReport,
    };
};
